import React, { Component, Fragment } from "react";
import { inject, observer } from "mobx-react";
import { Grid, Typography, Button, TextField, IconButton, Dialog, DialogContent, DialogTitle, DialogActions, Chip } from "@material-ui/core";
import Dropzone from "react-dropzone";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import VisibilityIcon from "@material-ui/icons/Visibility";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import DeleteIcon from "@material-ui/icons/Delete";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import ImageIcon from "@material-ui/icons/Image";
import DescriptionIcon from "@material-ui/icons/Description";
import RefreshIcon from "@material-ui/icons/Refresh";
import { Storage } from "aws-amplify";
import agent from "../../../agent";
import uniqid from "uniqid";

import "./BookingAttachments.css";

const FILE_MAXSIZE = 10485760; // 10MB
const MAX_ATTACHMENTS = 10;
const ACCEPTED_FILES = "application/pdf,image/*,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

@inject("bookingRequestStore", "profileStore", "templateStore")
@observer
class BookingAttachments extends Component {
    state = {
        previewOpen: false,
        previewFile: null,
        isLoadingAttachments: false,
        savedTitles: {},
        deletingIds: []
    };

    componentDidMount() {
        this.loadAttachments();
    }

    componentDidUpdate(prevProps) {
        const prevBookingId = prevProps.bookingRequestStore?.booking?.id;
        const currentBookingId = this.props.bookingRequestStore?.booking?.id;
        if (currentBookingId && currentBookingId !== prevBookingId) {
            this.loadAttachments();
        }
    }

    loadAttachments = async () => {
        const bookingId = this.props.bookingRequestStore?.booking?.id;
        if (!bookingId) return;

        this.setState({ isLoadingAttachments: true });
        try {
            const { currentProfile } = this.props.profileStore;
            const response = await agent.BookingAttachments.list(bookingId, currentProfile?.id);
            if (response?.attachments) {
                this.props.bookingRequestStore.setAttachments(response.attachments);
            }
        } catch (error) {
            console.error("Failed to load attachments:", error);
        } finally {
            this.setState({ isLoadingAttachments: false });
        }
    };

    getFileIcon = (fileType) => {
        if (!fileType) return <DescriptionIcon />;
        if (fileType.includes("pdf")) return <PictureAsPdfIcon />;
        if (fileType.includes("image")) return <ImageIcon />;
        return <DescriptionIcon />;
    };

    getFileIconClass = (fileType) => {
        if (!fileType) return "doc";
        if (fileType.includes("pdf")) return "pdf";
        if (fileType.includes("image")) return "image";
        return "doc";
    };

    handleFileDrop = (acceptedFiles, rejectedFiles) => {
        if (rejectedFiles && rejectedFiles.length > 0) {
            this.props.templateStore.openSnackbar("Some files couldn't be uploaded. Please check size (max 10MB) and format.");
            return;
        }

        const { attachments, stagedAttachments } = this.props.bookingRequestStore;
        const persistedCount = attachments ? attachments.length : 0;
        const stagedCount = stagedAttachments ? stagedAttachments.length : 0;
        const totalCount = persistedCount + stagedCount;

        if (totalCount + acceptedFiles.length > MAX_ATTACHMENTS) {
            this.props.templateStore.openSnackbar(`Maximum ${MAX_ATTACHMENTS} attachments allowed.`);
            return;
        }

        const bookingId = this.props.bookingRequestStore?.booking?.id;

        acceptedFiles.forEach(file => {
            const tempId = uniqid();
            const attachment = {
                id: tempId,
                fileName: file.name,
                title: "",
                fileType: file.type,
                fileSize: file.size,
                fileUrl: URL.createObjectURL(file),
                file: file,
                isUploading: bookingId ? true : false,
                uploadStatus: bookingId ? 'uploading' : 'staged',
                uploadedAt: new Date().toISOString()
            };

            if (bookingId) {
                this.props.bookingRequestStore.addAttachment(attachment);
                this.uploadFile(file, tempId);
            } else {
                this.props.bookingRequestStore.stageAttachment(attachment);
            }
        });
    };

    uploadFile = async (file, tempId) => {
        const bookingId = this.props.bookingRequestStore?.booking?.id;
        const profileId = this.props.profileStore?.currentProfile?.id;

        if (!profileId || !bookingId) {
            this.props.templateStore.openSnackbar("Profile or booking not loaded. Please try again.");
            this.props.bookingRequestStore.removeAttachment(tempId);
            return;
        }

        try {
            this.props.bookingRequestStore.setAttachmentUploadProgress(tempId, 10);

            const fileExtension = file.name.split('.').pop();
            const ticks = (((new Date()).getTime() * 10000) + 621355968000000000);
            const uniqueName = `${ticks}.${fileExtension}`;
            const storageKey = `bookingAttachment/${bookingId}/${uniqueName}`;

            this.props.bookingRequestStore.setAttachmentUploadProgress(tempId, 30);

            const result = await Storage.put(storageKey, file, {
                contentType: file.type
            });

            this.props.bookingRequestStore.setAttachmentUploadProgress(tempId, 70);

            const confirmResponse = await agent.BookingAttachments.confirmUpload(bookingId, profileId, {
                storageKey: result.key,
                fileName: file.name,
                fileType: file.type,
                fileSize: file.size,
                title: ""
            });

            this.props.bookingRequestStore.setAttachmentUploadProgress(tempId, 100);

            const savedAttachment = confirmResponse.attachment;
            this.props.bookingRequestStore.replaceAttachment(tempId, {
                id: savedAttachment.id,
                fileName: savedAttachment.fileName,
                title: savedAttachment.title || "",
                fileType: savedAttachment.fileType,
                fileSize: savedAttachment.fileSize,
                fileUrl: savedAttachment.fileUrl,
                uploadedAt: savedAttachment.uploadedAt,
                isUploading: false
            });

        } catch (error) {
            console.error("Upload failed - Full error:", error);
            console.error("Error response:", error?.response);
            console.error("Error message:", error?.message);
            let errorMessage = "File upload failed. Please try again.";
            if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error?.message) {
                errorMessage = error.message;
            } else if (typeof error === 'string') {
                errorMessage = error;
            }
            this.props.templateStore.openSnackbar(errorMessage);
            this.props.bookingRequestStore.removeAttachment(tempId);
        }
    };

    handleTitleChange = (attachmentId, title, isStaged = false) => {
        if (isStaged) {
            this.props.bookingRequestStore.updateStagedAttachmentTitle(attachmentId, title);
        } else {
            this.props.bookingRequestStore.updateAttachmentTitle(attachmentId, title);
        }
    };

    handleTitleBlur = async (attachmentId) => {
        const bookingId = this.props.bookingRequestStore?.booking?.id;
        if (!bookingId || typeof attachmentId !== 'number') return;

        const { attachments } = this.props.bookingRequestStore;
        const attachment = attachments?.find(a => a.id === attachmentId);
        if (!attachment || attachment.isUploading) return;

        try {
            await agent.BookingAttachments.updateTitle(bookingId, attachmentId, {
                title: attachment.title || ""
            });
            this.setState(prev => ({
                savedTitles: { ...prev.savedTitles, [attachmentId]: true }
            }));
            setTimeout(() => {
                this.setState(prev => {
                    const updated = { ...prev.savedTitles };
                    delete updated[attachmentId];
                    return { savedTitles: updated };
                });
            }, 2000);
        } catch (error) {
            console.error("Failed to update title:", error);
            this.props.templateStore.openSnackbar("Failed to save title.");
        }
    };

    handleDelete = async (attachmentId, isStaged = false) => {
        if (isStaged) {
            this.setState(prev => ({
                deletingIds: [...prev.deletingIds, attachmentId]
            }));
            setTimeout(() => {
                this.props.bookingRequestStore.removeStagedAttachment(attachmentId);
                this.setState(prev => ({
                    deletingIds: prev.deletingIds.filter(id => id !== attachmentId)
                }));
            }, 300);
            return;
        }

        const bookingId = this.props.bookingRequestStore?.booking?.id;
        const { attachments } = this.props.bookingRequestStore;
        const attachment = attachments?.find(a => a.id === attachmentId);

        if (attachment?.isUploading) {
            this.props.bookingRequestStore.removeAttachment(attachmentId);
            return;
        }

        if (!bookingId) {
            this.props.bookingRequestStore.removeAttachment(attachmentId);
            return;
        }

        this.setState(prev => ({
            deletingIds: [...prev.deletingIds, attachmentId]
        }));

        try {
            await agent.BookingAttachments.delete(bookingId, attachmentId);
            setTimeout(() => {
                this.props.bookingRequestStore.removeAttachment(attachmentId);
                this.setState(prev => ({
                    deletingIds: prev.deletingIds.filter(id => id !== attachmentId)
                }));
                this.props.templateStore.openSnackbar("Attachment deleted.");
            }, 300);
        } catch (error) {
            console.error("Delete failed:", error);
            this.setState(prev => ({
                deletingIds: prev.deletingIds.filter(id => id !== attachmentId)
            }));
            const errorMessage = error?.response?.data?.message || "Failed to delete attachment.";
            this.props.templateStore.openSnackbar(errorMessage);
        }
    };

    handleRetryUpload = async (stagedAttachment) => {
        const bookingId = this.props.bookingRequestStore?.booking?.id;
        const profileId = this.props.profileStore?.currentProfile?.id;

        if (!bookingId || !profileId) {
            this.props.templateStore.openSnackbar("Cannot retry - booking not saved yet.");
            return;
        }

        this.props.bookingRequestStore.setStagedAttachmentStatus(stagedAttachment.id, 'uploading');
        
        try {
            this.props.bookingRequestStore.setAttachmentUploadProgress(stagedAttachment.id, 10);

            const fileExtension = stagedAttachment.fileName.split('.').pop();
            const ticks = (((new Date()).getTime() * 10000) + 621355968000000000);
            const uniqueName = `${ticks}.${fileExtension}`;
            const storageKey = `bookingAttachment/${bookingId}/${uniqueName}`;

            this.props.bookingRequestStore.setAttachmentUploadProgress(stagedAttachment.id, 30);

            const result = await Storage.put(storageKey, stagedAttachment.file, {
                contentType: stagedAttachment.fileType
            });

            this.props.bookingRequestStore.setAttachmentUploadProgress(stagedAttachment.id, 70);

            const confirmResponse = await agent.BookingAttachments.confirmUpload(bookingId, profileId, {
                storageKey: result.key,
                fileName: stagedAttachment.fileName,
                fileType: stagedAttachment.fileType,
                fileSize: stagedAttachment.fileSize,
                title: stagedAttachment.title || ""
            });

            this.props.bookingRequestStore.setAttachmentUploadProgress(stagedAttachment.id, 100);

            const savedAttachment = confirmResponse.attachment;
            this.props.bookingRequestStore.removeStagedAttachment(stagedAttachment.id);
            this.props.bookingRequestStore.addAttachment({
                id: savedAttachment.id,
                fileName: savedAttachment.fileName,
                title: savedAttachment.title || "",
                fileType: savedAttachment.fileType,
                fileSize: savedAttachment.fileSize,
                fileUrl: savedAttachment.fileUrl,
                uploadedAt: savedAttachment.uploadedAt,
                isUploading: false
            });

        } catch (error) {
            console.error("Retry upload failed:", error);
            this.props.bookingRequestStore.setStagedAttachmentStatus(stagedAttachment.id, 'error', error?.message || 'Upload failed');
            this.props.templateStore.openSnackbar("Retry failed. Please try again.");
        }
    };

    handleView = (attachment) => {
        this.setState({
            previewOpen: true,
            previewFile: attachment
        });
    };

    handleClosePreview = () => {
        this.setState({
            previewOpen: false,
            previewFile: null
        });
    };

    handleDownload = async (attachment) => {
        try {
            if (attachment.fileUrl) {
                window.open(attachment.fileUrl, '_blank');
            } else if (attachment.file) {
                const url = URL.createObjectURL(attachment.file);
                window.open(url, '_blank');
            }
        } catch (error) {
            console.error("Download failed:", error);
            this.props.templateStore.openSnackbar("Download failed. Please try again.");
        }
    };

    formatFileSize = (bytes) => {
        if (bytes === 0) return "0 Bytes";
        const k = 1024;
        const sizes = ["Bytes", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    };

    renderPreviewContent = () => {
        const { previewFile } = this.state;
        if (!previewFile) return null;

        const fileType = previewFile.fileType || "";

        if (fileType.includes("image")) {
            return (
                <img 
                    src={previewFile.fileUrl || (previewFile.file ? URL.createObjectURL(previewFile.file) : "")} 
                    alt={previewFile.title || previewFile.fileName}
                    className="preview-image"
                />
            );
        }

        if (fileType.includes("pdf")) {
            return (
                <div className="pdf-preview-placeholder">
                    <PictureAsPdfIcon style={{ fontSize: 80, color: "#c62828" }} />
                    <Typography variant="body1" style={{ marginTop: 16 }}>PDF Preview</Typography>
                    <Typography variant="caption" color="textSecondary">
                        Click Download to view the full document
                    </Typography>
                </div>
            );
        }

        return (
            <div className="doc-preview-placeholder">
                <DescriptionIcon style={{ fontSize: 80, color: "#1565c0" }} />
                <Typography variant="body1" style={{ marginTop: 16 }}>Document Preview</Typography>
                <Typography variant="caption" color="textSecondary">
                    Click Download to view the full document
                </Typography>
            </div>
        );
    };

    renderAttachmentItem = (attachment, isStaged = false) => {
        const { attachmentUploadProgress } = this.props.bookingRequestStore;
        const { savedTitles, deletingIds } = this.state;
        const readonly = this.props.readOnly;
        const isUploading = attachment.isUploading || attachment.uploadStatus === 'uploading';
        const hasError = attachment.uploadStatus === 'error';
        const isPending = attachment.uploadStatus === 'staged';
        const isDeleting = deletingIds.includes(attachment.id);
        const isSaved = savedTitles[attachment.id];

        return (
            <div key={attachment.id} className={`attachment-item ${isUploading ? "uploading" : ""} ${hasError ? "error" : ""} ${isDeleting ? "deleting" : ""}`}>
                {attachment.fileType && attachment.fileType.includes("image") && attachment.fileUrl ? (
                    <img 
                        src={attachment.fileUrl} 
                        alt={attachment.title || attachment.fileName}
                        className="attachment-thumbnail"
                        onClick={() => this.handleView(attachment)}
                    />
                ) : (
                    <div className={`attachment-icon ${this.getFileIconClass(attachment.fileType)}`}>
                        {this.getFileIcon(attachment.fileType)}
                    </div>
                )}
                
                <div className="attachment-details">
                    {readonly ? (
                        <Typography className="attachment-title-readonly">
                            {attachment.title || attachment.fileName}
                        </Typography>
                    ) : (
                        <div className="attachment-title-row">
                            <TextField
                                value={attachment.title || ""}
                                onChange={(e) => this.handleTitleChange(attachment.id, e.target.value, isStaged)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !isStaged) {
                                        e.preventDefault();
                                        this.handleTitleBlur(attachment.id);
                                    }
                                }}
                                placeholder="Enter file title... (Press Enter to save)"
                                fullWidth
                                margin="none"
                                className="attachment-title-input"
                                disabled={isUploading}
                            />
                            {isSaved && (
                                <span className="saved-indicator">
                                    <CheckCircleIcon /> Saved
                                </span>
                            )}
                        </div>
                    )}
                    <div className="attachment-meta">
                        <span 
                            className="attachment-filename"
                            onClick={() => !isUploading && this.handleView(attachment)}
                        >
                            {attachment.fileName}
                        </span>
                        <span>•</span>
                        <span>{this.formatFileSize(attachment.fileSize)}</span>
                        {isPending && (
                            <>
                                <span>•</span>
                                <Chip 
                                    label="Ready to upload" 
                                    size="small" 
                                    className="status-chip pending"
                                />
                            </>
                        )}
                        {isUploading && (
                            <>
                                <span>•</span>
                                <span>Uploading...</span>
                            </>
                        )}
                        {hasError && (
                            <>
                                <span>•</span>
                                <Chip 
                                    label="Upload failed" 
                                    size="small" 
                                    className="status-chip error"
                                />
                            </>
                        )}
                    </div>
                    {isUploading && (
                        <div className="progress-bar">
                            <div 
                                className="progress" 
                                style={{ width: `${attachmentUploadProgress[attachment.id] || 0}%` }}
                            />
                        </div>
                    )}
                </div>
                
                <div className="attachment-actions">
                    {!isUploading && !isPending && !hasError && (
                        <>
                            <IconButton 
                                className="action-btn view"
                                onClick={() => this.handleView(attachment)}
                                title="View File"
                                size="small"
                            >
                                <VisibilityIcon />
                            </IconButton>
                            <IconButton 
                                className="action-btn download"
                                onClick={() => this.handleDownload(attachment)}
                                title="Download File"
                                size="small"
                            >
                                <CloudDownloadIcon />
                            </IconButton>
                        </>
                    )}
                    {hasError && (
                        <IconButton 
                            className="action-btn retry"
                            onClick={() => this.handleRetryUpload(attachment)}
                            title="Retry Upload"
                            size="small"
                        >
                            <RefreshIcon />
                        </IconButton>
                    )}
                    {!readonly && (
                        <IconButton 
                            className="action-btn delete"
                            onClick={() => this.handleDelete(attachment.id, isStaged)}
                            title={isUploading ? "Cancel Upload" : "Delete File"}
                            size="small"
                        >
                            {isUploading ? <CloseIcon /> : <DeleteIcon />}
                        </IconButton>
                    )}
                </div>
            </div>
        );
    };

    render() {
        const { attachments, stagedAttachments } = this.props.bookingRequestStore;
        const { previewOpen, previewFile, isLoadingAttachments } = this.state;
        const readonly = this.props.readOnly;
        const persistedList = attachments || [];
        const stagedList = stagedAttachments || [];
        const totalCount = persistedList.length + stagedList.length;
        const bookingId = this.props.bookingRequestStore?.booking?.id;

        return (
            <Fragment>
                <Grid container spacing={8}>
                    {!readonly && (
                        <Grid item xs={12}>
                            <Dropzone
                                accept={ACCEPTED_FILES}
                                maxSize={FILE_MAXSIZE}
                                onDrop={this.handleFileDrop}
                                multiple={true}
                                className="booking-attachments-dropzone"
                                disabled={totalCount >= MAX_ATTACHMENTS}
                            >
                                <CloudUploadIcon className="upload-icon" />
                                <Typography className="upload-text">
                                    Drag and drop files here, or click to browse
                                </Typography>
                                <Typography variant="caption" className="upload-hint">
                                    PDF, Images, Documents • Max 10MB per file • {totalCount}/{MAX_ATTACHMENTS} files
                                    {!bookingId && stagedList.length > 0 && (
                                        <span className="staged-hint"> • Files will upload when booking is saved</span>
                                    )}
                                </Typography>
                            </Dropzone>
                        </Grid>
                    )}

                    {isLoadingAttachments && (
                        <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary">
                                Loading attachments...
                            </Typography>
                        </Grid>
                    )}

                    {(persistedList.length > 0 || stagedList.length > 0) && (
                        <Grid item xs={12}>
                            <div className="attachments-list">
                                {stagedList.map((attachment) => this.renderAttachmentItem(attachment, true))}
                                {persistedList.map((attachment) => this.renderAttachmentItem(attachment, false))}
                            </div>
                        </Grid>
                    )}

                    {readonly && persistedList.length === 0 && stagedList.length === 0 && !isLoadingAttachments && (
                        <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
                                No attachments for this booking.
                            </Typography>
                        </Grid>
                    )}
                </Grid>

                {/* File Preview Modal */}
                <Dialog
                    open={previewOpen}
                    onClose={this.handleClosePreview}
                    maxWidth="md"
                    fullWidth
                    className="attachment-preview-dialog"
                >
                    <DialogTitle className="preview-dialog-title">
                        <div className="preview-title-content">
                            {previewFile && this.getFileIcon(previewFile.fileType)}
                            <span>{previewFile?.title || previewFile?.fileName}</span>
                        </div>
                        <IconButton onClick={this.handleClosePreview} size="small">
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent className="preview-dialog-content">
                        {this.renderPreviewContent()}
                    </DialogContent>
                    <DialogActions className="preview-dialog-actions">
                        <Typography variant="caption" color="textSecondary" style={{ flex: 1 }}>
                            {previewFile?.fileName} • {previewFile && this.formatFileSize(previewFile.fileSize)}
                        </Typography>
                        <Button onClick={this.handleClosePreview} color="default">
                            Close
                        </Button>
                        <Button 
                            onClick={() => previewFile && this.handleDownload(previewFile)} 
                            color="primary" 
                            variant="contained"
                        >
                            <CloudDownloadIcon style={{ marginRight: 8 }} />
                            Download
                        </Button>
                    </DialogActions>
                </Dialog>
            </Fragment>
        );
    }
}

export default BookingAttachments;
